/**
 * Configuration object to control dev testing features.
 * Modify these values to enable/disable specific dev behaviors.
 */
const DEV_CONFIG = {
  /** Enable/disable testing headers in GraphQL requests */
  enableTestingHeaders: true,
  /** Enable/disable request logging (for debugging) */
  enableRequestLogging: false,
  /**
   * Enable/disable the entire interceptor system
   * Set to false if experiencing performance issues in dev
   */
  enableInterceptor: true,
} as const;

/**
 * Determines if the current environment is a development environment.
 * Checks if the hostname contains 'dev'.
 *
 * @returns {boolean} True if running in development environment, false otherwise
 *
 * @example
 * ```tsx
 * if (isDev()) {
 *   console.log('Running in development mode');
 * }
 * ```
 */
export const isDev = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname.includes('dev')) {
    return true;
  }
  return false;
};

/**
 * Installs global fetch and XHR interceptors that automatically add testing headers
 * to all HTTP requests when in development environment.
 *
 * Call this once during app initialization.
 */
export const installGlobalHeaderInterceptor = () => {
  console.log('🔧 installGlobalHeaderInterceptor called');

  // If any of the conditions are not met, do not install the interceptor
  if (!isDev() || !DEV_CONFIG.enableTestingHeaders || !DEV_CONFIG.enableInterceptor) {
    return;
  }

  // Intercept fetch requests (used by GraphQL/modern APIs)
  const originalFetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const urlString = typeof input === 'string' ? input : input.toString();

    // Quick filter: only process relevant requests
    const isBusinessAPI = urlString.includes('/api/') || urlString.includes('graphql');
    const isEventing = urlString.includes('eventing');
    const shouldAddHeader = isBusinessAPI && !isEventing;

    if (DEV_CONFIG.enableRequestLogging && shouldAddHeader) {
      console.log('🔧 Fetch will get header:', urlString);
    }

    if (shouldAddHeader) {
      const headers = new Headers(init.headers);
      headers.set('x-coursera-testing', 'true');

      return originalFetch(input, {
        ...init,
        headers,
      });
    }

    return originalFetch(input, init);
  };

  // Intercept XMLHttpRequest (used Naptime)
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    const urlString = typeof url === 'string' ? url : url.toString();

    // Quick filter: only process potentially relevant requests
    const isBusinessAPI = urlString.includes('/api/') || urlString.includes('graphql');
    const isEventing = urlString.includes('eventing');

    // Store decision for send() - minimize property access
    (this as any)._courseraAddHeader = isBusinessAPI && !isEventing;

    // Optional logging (disabled by default for performance)
    if (DEV_CONFIG.enableRequestLogging && (this as any)._courseraAddHeader) {
      console.log('🔧 XHR will get header:', method, urlString);
    }

    return originalXHROpen.call(this, method, url, async ?? true, username, password);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    // Single property check - fastest possible path
    if ((this as any)._courseraAddHeader) {
      this.setRequestHeader('x-coursera-testing', 'true');
    }

    return originalXHRSend.call(this, body);
  };

  console.log('🔧 Dev mode: Global testing headers interceptor installed successfully (fetch + XHR)');
};
